import { styled, Tooltip, Typography } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AppointmentApi, { AppointmentInterface } from 'api/counter/appointment.api';
import ClinicApi, { DentistInterface } from 'api/master/clinic.api';
import InputCheckbox from 'component/Input/InputCheckbox';
import AutocompleteSelect from 'component/Select/AutocompleteSelect';
import TableCustom, { TablePaginationMiniCustom } from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';
import { CustomFilterDoctor, ListFilterDoctors } from 'features/counter/appointment/head-appointment';
import _, { floor } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { dateToView } from 'utils/date.utils';
import UseWindowSize from 'utils/useWindowSize';

import { faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker';
import { notiError } from 'component/notifications/notifications';
import { icons } from 'constants/images';
import { colors, ThemeIcon } from 'constants/theme';
import { BoxStatus, TextStatus } from 'features/home/UseStyled';
import MainButton from 'new-components/buttons/main-button';
import NewIconButton from 'new-components/buttons/new-icon-button';
import SelectInput from 'new-components/inputs/select-input';
import PageModal from 'new-components/modal/page-modal';
import { Image } from 'react-bootstrap';
import { getBranch } from 'utils/app.utils';
export interface IViewHistoryProps {
  onClose: () => void
  onPrint: (e: any) => void
  patientId: string
  /** เมื่อส่งมา จะแสดง checkbox เลือกหลายแถว และปุ่มพิมพ์ OPD Card รวมเป็นไฟล์เดียว */
  onPrintBulk?: (appointmentIds: number[]) => void
  /** กำลังพิมพ์ OPD อยู่ แสดง loading overlay ใน modal */
  loadingPrint?: boolean
  /** ช่วงวันที่เริ่มต้น (ตามเมนูหลัก) เช่น จากหน้านัดหมายส่ง filterDate มา */
  defaultRangeDate?: [string, string]
}

/** STYLE CUSTOM */
const TableConfig = styled('div')(({ theme }) => ({
  td: { height: '48px' },
  '.MuiTableBody-root': {
    '.MuiTableCell-root': {
      verticalAlign: 'top !important',
      paddingBottom: '12px !important'
    }
  },
  '& .appointmentOperatives': { maxWidth: '200px' },
  '& .practice': { minWidth: '300px', maxWidth: '500px' },
  '& .remark': { minWidth: '300px', maxWidth: '500px' },
  '.MuiTableContainer-root': {
    maxHeight: 'calc(56px + 10 * 52px) !important',
    minHeight: 'calc(56px + 10 * 52px) !important',
    overflowY: 'auto !important',
  },
  '.MuiPaper-root table thead': {
    backgroundColor: `${colors.themeMainColor} !important`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  '.MuiPaper-root table thead tr th': {
    backgroundColor: `${colors.themeMainColor} !important`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  }
}))

const THead = styled('div')(({ theme }) => ({
  display: 'flex',
  backgroundColor: colors.themeMainColor,
  borderRadius: '8px 8px 0 0',
  padding: '0.5rem 1.5rem',
  fontSize: 16,
  color: colors.white
}))

const TBody = styled('div')(({ theme }) => ({
  border: `1px solid ${colors.lightGray}`,
  borderTopWidth: 0,
  color: colors.textPrimary,
  fontSize: 16,
  padding: '0.625rem 1.5rem',
  '.expand-more': {
    backgroundColor: colors.white,
    border: `1px solid ${colors.textLightGray}`,
    color: colors.themeMainColor,
    borderRadius: 100,
    fontSize: 22
  }
}))

const Cell = styled('div')(({ theme }) => ({ '&:first-of-type': { marginRight: '1.25rem', width: '100%' } }))

export default function ViewAppointment(props: IViewHistoryProps) {
  const { t } = useTranslation()
  const initialRange: [string, string] = (props.defaultRangeDate?.[0] && props.defaultRangeDate?.[1])
    ? props.defaultRangeDate
    : (emptyRangePicker as [string, string])

  /** statusIcon map - same pattern as view-history.tsx */
  const statusIcon: any = {
    NORMAL: { icon: <FontAwesomeIcon style={{ color: colors.white }} icon={faUser} />, color: colors.appointmentStatus.statusNormal, label: t('APPOINTMENT.STATUS.NORMAL') },
    CANCEL_APP: { icon: <Image style={{ height: '15px' }} src={icons.appointment.cancel_app} />, color: colors.appointmentStatus.statusCancel, label: t('APPOINTMENT.STATUS.CANCEL') },
    CANCEL: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconCancel} />, color: colors.appointmentStatus.statusCancel, label: t('APPOINTMENT.STATUS.CANCEL') },
    CAN_NOT_CONTACT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconNotConnect} />, color: colors.appointmentStatus.statusUnConnect, label: t('APPOINTMENT.STATUS.CAN_NOT_CONTACT') },
    PUT_OFF: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconChangeSchedule} />, color: colors.appointmentStatus.statusChange, label: t('APPOINTMENT.STATUS.PUT_OFF') },
    CONFIRM: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconEnter} />, color: colors.appointmentStatus.statusEnter, label: t('APPOINTMENT.STATUS.CONFIRM') },
    ARRIVED: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconUserCheckTrue} />, color: colors.appointmentStatus.statusArrived, label: t('APPOINTMENT.STATUS.ARRIVED') },
    NOT_YET: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconUserCheckFalse} />, color: colors.appointmentStatus.statusUnArrived, label: t('APPOINTMENT.STATUS.NOT_YET') },
    PAYMENT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconPendingPayment} />, color: colors.appointmentStatus.statusWaitPayment, label: t('APPOINTMENT.STATUS.PAYMENT') },
    PENDING_PAYMENT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconWaitPayment} />, color: colors.appointmentStatus.statusPayment, label: t('APPOINTMENT.STATUS.PENDING_PAYMENT') },
    PENDING_SERVICE: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconCounterCheckIn} />, color: colors.appointmentStatus.statusCounterCheckIn, label: t('APPOINTMENT.STATUS.PENDING_SERVICE') },
    SERVICE: { icon: <Image style={{ height: '17px' }} src={icons.appointment.iconService} />, color: colors.appointmentStatus.statusService, label: t('APPOINTMENT.STATUS.SERVICE') },
    DONE: { icon: <Image style={{ height: '15px' }} src={ThemeIcon.DoneTreatment} />, color: colors.appointmentStatus.statusDone, label: t('APPOINTMENT.STATUS.DONE') },
  }

  const renderAppointStatus = (status: string) => {
    return <BoxStatus style={{ marginLeft: '5px', whiteSpace: 'nowrap', minWidth: 80 }} bg={statusIcon[status]?.color || ''}>
      {statusIcon[status]?.icon || ''}
      <TextStatus>
        {statusIcon[status]?.label || ''}
      </TextStatus>
    </BoxStatus>
  }

  const renderPrintStatus = (isPrinted: string | undefined) => {
    const printed = isPrinted === '1'
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', color: printed ? colors.appointmentStatus.statusEnter : colors.textLightGray }}>
      {printed
        ? <CheckCircleIcon sx={{ fontSize: 16 }} />
        : <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />}
      {printed ? t('APPOINTMENT.PRINTED') : t('APPOINTMENT.NOT_PRINTED')}
    </span>
  }

  const [doctors, setDoctors] = useState<DentistInterface[]>([])
  const [appointments, setAppointments] = useState<AppointmentInterface[]>([])
  const [width] = UseWindowSize()
  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('date')
  const [sortType, setSortType] = useState('DESC')
  const [status, setStatus] = useState<boolean>(true)

  /** FILTER */
  const [search, setSearch] = useState('')
  const [doctor, setDoctor] = useState<number>(0)
  const [rangeDate, setRangeDate] = useState<[string, string]>(initialRange)
  const [filterStatus, setFilterStatus] = useState<string>('ALL')

  const [fetch, setFetch] = useState<boolean>(true);
  const [conditionLoad, setConditionLoad] = useState<any>({})

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    branchId: getBranch() || 'ALL',
    rangeDate: initialRange,
    doctor: 0,
    statusAppointment: 'ALL' as string
  })

  const loadDataAppointment = async () => {
    setFetch(true)
    try {
      let condition: any = {}
      if (page) condition = { ...condition, page: page }
      if (pageLimit) condition = { ...condition, pageLimit: pageLimit || 10 }
      if (sortBy) condition = { ...condition, sortBy: sortBy }
      if (sortType) condition = { ...condition, sortType: sortType }
      if (appliedFilters.branchId !== 'ALL') condition = { ...condition, branchId: appliedFilters.branchId }
      if (appliedFilters.doctor) condition = { ...condition, doctorId: appliedFilters.doctor }
      if (appliedFilters.statusAppointment && appliedFilters.statusAppointment !== 'ALL') condition = { ...condition, statusAppointment: appliedFilters.statusAppointment }
      if (props.patientId) condition = { ...condition, patientId: props.patientId }
      if (appliedFilters.rangeDate?.[0] && appliedFilters.rangeDate?.[1]) {
        condition = { ...condition, dateStart: appliedFilters.rangeDate[0], dateEnd: appliedFilters.rangeDate[1] }
      }
      await AppointmentApi.findAll(condition).then(({ data, headers }) => {
        if (data) {
          const newData: any = data.sort((a: any, b: any) => {
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          });
          setRowCount(headers['x-total'])
          setAppointments(newData)
          if (fetch || status) {
            setConditionLoad(condition)
          }
          setStatus(false)
        }
      }).finally(() => {
        setFetch(false)
      })
    } catch (error) {
      notiError('ERROR')
    }
  }


  const prevLoadingPrint = useRef(false)
  useEffect(() => {
    if (prevLoadingPrint.current && !props.loadingPrint) {
      loadDataAppointment()
    }
    prevLoadingPrint.current = !!props.loadingPrint
  }, [props.loadingPrint])

  useEffect(() => {
    loadDataAppointment()
  }, [page, pageLimit, sortBy, sortType, appliedFilters])

  const searchCilck = (() => {
    setPage(1)
    setAppliedFilters({ search: search, branchId, rangeDate: rangeDate, doctor, statusAppointment: filterStatus })
  })

  const loadData = useCallback(() => { ClinicApi.findAllDentists().then(({ data }) => setDoctors(data)) }, [])

  const branchData = useCallback(() => { ClinicApi.findAllBranches().then(({ data }) => setBranches(_.map(data, (item) => ({ value: item.branchId, name: item.branchName || item.branchNameEn })))) }, [])
  useEffect(() => {
    loadData()
    branchData()
  }, [])


  const showBulkPrint = Boolean(props.onPrintBulk)
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  // API คืนเฉพาะข้อมูลหน้านั้นแล้ว ไม่ต้อง slice
  const pageAppointmentIds = appointments.map((a) => a.appointmentId)
  const allPageSelected = pageAppointmentIds.length > 0 && pageAppointmentIds.every((id) => selectedIds.has(id))
  const somePageSelected = pageAppointmentIds.some((id) => selectedIds.has(id))
  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const n = new Set(prev)
        pageAppointmentIds.forEach((id) => n.delete(id))
        return n
      })
    } else {
      setSelectedIds((prev) => {
        const n = new Set(prev)
        pageAppointmentIds.forEach((id) => n.add(id))
        return n
      })
    }
  }

  const headCells = [
    ...(showBulkPrint ? [{
      id: 'select',
      disablePadding: true,
      label: 'CHECKBOX',
      width: '48px',
      checked: allPageSelected,
      indeterminate: somePageSelected && !allPageSelected,
      onCheckAll: toggleSelectAll,
      values: []
    }] : []),
    { id: 'no', disablePadding: false, label: t('TABLE.NO'), class: 'text-nowrap' },
    { id: 'branchId', disablePadding: false, label: t('APPOINTMENT.HISTORY.BRANCH'), class: 'text-nowrap' },
    { id: 'date', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_DATE'), class: 'text-nowrap' },
    { id: 'timeStart', disablePadding: false, label: t('APPOINTMENT.HISTORY.TIME_START'), class: 'text-nowrap' },
    { id: 'durationMinute', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_DURATION'), class: 'text-nowrap' },
    { id: 'doctorFullname', disablePadding: false, label: t('APPOINTMENT.HISTORY.DOCTOR'), class: 'text-nowrap', width: '12rem' },
    { id: 'appointmentOperatives', disablePadding: false, label: t('APPOINTMENT.HISTORY.SERVICE'), class: 'text-nowrap', width: '30rem' },
    { id: 'remark', disablePadding: false, label: t('APPOINTMENT.HISTORY.REMARK'), class: 'text-nowrap', width: '15rem' },
    { id: 'statusAppointment', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_STATUS'), class: 'text-nowrap text-center', width: '8rem' },
    { id: 'isPrintedOPD', disablePadding: false, label: t('APPOINTMENT.PRINT_STATUS'), class: 'text-nowrap text-center', width: '8rem' },
    { id: 'action', disablePadding: false, label: t('APPOINTMENT.HISTORY.ACTION'), width: '6rem' },
  ]

  const getDurationTextByTimeMinute = (minute: number) => {
    if (minute >= 60) return `${floor(minute / 60)} ${t('TIME_DURATION_TYPE.HOURS')} ` + (minute % 60 > 0 ? `${minute % 60} ${t('TIME_DURATION_TYPE.MINUTES')}` : '')
    else return `${minute} ${t('TIME_DURATION_TYPE.MINUTES')}`
  }

  const renderDataSlim = (objData: AppointmentInterface, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentId, branchName, date, timeStart, durationMinute, cnNumber, branchCnNumber, patientFullname, doctorFullname, appointmentOperatives, remark } = objData
    return (
      <TBody>
        <div className={'d-flex align-items-center'}>
          <Cell>
            <Row className="pl-4 mx-0">
              {showBulkPrint && (
                <Col xs={12} className="mb-2">
                  <InputCheckbox checked={selectedIds.has(appointmentId)} onChange={() => toggleSelect(appointmentId)} />
                </Col>
              )}
              <div className="position-absolute w-auto px-0" style={{ marginLeft: '-1.5rem' }}>{no}</div>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.BRANCH')}</Col>
              <Col xs={6} sm={3}>{branchName}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.APPOINTMENT_DATE')}</Col>
              <Col xs={6} sm={3}>{dateToView(date)}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.TIME_START')}</Col>
              <Col xs={6} sm={3}>{`${timeStart.replace(':00', '').replace(':', '.')} ${t('TIME_UNIT')}`}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.DOCTOR')}</Col>
              <Col xs={6} sm={3}>{doctorFullname || '-'}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.SERVICE')}</Col>
              <Col xs={6} sm={3}>
                <Tooltip title={String(_.map(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName).join(', '))} placement="left">
                  <div>{String(_.map(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName).join(', '))}</div>
                </Tooltip>
              </Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.REMARK')}</Col>
              <Col xs={6} sm={3} className={'hard-break'}>{remark || '-'}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.APPOINTMENT_STATUS')}</Col>
              <Col xs={6} sm={3}>
                {renderAppointStatus(objData.statusAppointment)}
              </Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.PRINT_STATUS')}</Col>
              <Col xs={6} sm={3}>
                {renderPrintStatus(objData.isPrintedOPD)}
              </Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.ACTION')}</Col>
              <Col xs={6} sm={3} className="-font-weight-bold">
                <NewIconButton icon={<PrintOutlinedIcon />} onClick={() => props.onPrint(appointmentId)} size='medium' type='primary' variant='outlined' />
              </Col>
            </Row>
          </Cell>
        </div>
      </TBody>
    )
  }


  const renderData = (objData: AppointmentInterface, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentId, branchName, date, timeStart, durationMinute,
      doctorFullname, appointmentOperatives,
      remark } = objData
    const objRenderData = {
      key: String(appointmentId),
      id: String(appointmentId),
      obj: objData,
      columns: [
        ...(showBulkPrint ? [{
          option: 'COMPONENT',
          align: 'center' as const,
          component: (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <InputCheckbox
                checked={selectedIds.has(appointmentId)}
                onChange={() => toggleSelect(appointmentId)}
              />
            </div>
          )
        }] : []),
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: branchName || '', class: 'text-nowrap-ellipsis' },
        { option: 'TEXT', align: 'left', label: dateToView(date), class: 'text-nowrap-ellipsis' },
        { option: 'TEXT', align: 'center', label: `${timeStart.replace(':00', '').replace(':', '.')} น.` },
        { option: 'TEXT', align: 'center', label: getDurationTextByTimeMinute(durationMinute) },
        { option: 'TEXT', align: 'left', label: doctorFullname, class: 'text-nowrap-ellipsis' },
        {
          option: 'COMPONENT', align: 'left', component: (<div>
            <Tooltip title={String(_.map(_.filter(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName), (op: any) => op.operativeName || op.operativeTypeName).join(', '))} placement='bottom'>
              <div>{String(_.map(_.filter(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName), (op: any) => op.operativeName || op.operativeTypeName).join(', '))}</div>
            </Tooltip>
          </div>)
        },
        { option: 'TEXT', align: 'left', label: remark },
        {
          option: 'COMPONENT', align: 'center', component:
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {renderAppointStatus(objData.statusAppointment)}
            </div>
        },
        {
          option: 'COMPONENT', align: 'center', component:
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {renderPrintStatus(objData.isPrintedOPD)}
            </div>
        },
        {
          option: 'COMPONENT', align: 'center', component:
            <div style={{ display: 'flex', justifyContent: 'center' }}><NewIconButton icon={<PrintOutlinedIcon />} onClick={() => props.onPrint(appointmentId)} size='medium' type='primary' variant='outlined' /></div>
        }
      ]
    }
    return <TableRowCommon {...objRenderData} />
  }

  return <PageModal
    isOpen
    size={'L'}
    title={t('BRANCH.INPUT.OPD_APPOINTMENT')}
    handleClose={props.loadingPrint ? () => null : props.onClose}
    onCloseSubmit
    onSubmit={{ onClick: () => null }}
    onCancel={{ onClick: props.loadingPrint ? () => null : props.onClose, disabled: props.loadingPrint }}
    sx={{ zIndex: 2000 }}
  >
    <Box sx={{ minHeight: 300 }}>
      {props.loadingPrint && (
        <Backdrop
          open
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.5)',
            borderRadius: '10px'
          }}
          invisible={false}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <>
        <Row className="mb-3 mx-0 mt-2 g-2 align-items-end">
          <Col xs={12} sm={6} md={4} xl className="px-1 mb-2">
            <SelectInput
              onChange={(e) => {
                setBranchId(e.target.value)
                setPage(1)
              }}
              options={[{ value: 'ALL', name: t('STATUS.ALL') }, ...branches]}
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
            />
          </Col>
          <Col xs={12} sm={6} md={4} xl className="px-1 mb-2">
            <CustomFilterDoctor className='position-relative' >
              <Typography className='position-absolute edit-filter-doctor'>{t('USER_DOCTOR')}:</Typography>
              <AutocompleteSelect
                labelId="select-dentist"
                options={doctors}
                getOptionLabel={(option) => option.dentistFullname || t('ALL')}
                renderOption={(props, option) => {
                  return (
                    <ListFilterDoctors {...props} key={option.dentistId} component="li">
                      {option.dentistFullname}
                    </ListFilterDoctors>
                  )
                }}
                onchange={(e, value) => setDoctor(value?.dentistId)}
                noOptionsText={t('NOT_FOUND')}
                filterOptions={(option) => option.dentistFullname}
                value={_.find(doctors, { dentistId: doctor }) || ''}
                height={40}
                disableClearable={!doctor}
              />
            </CustomFilterDoctor>
          </Col>
          <Col xs={12} sm={6} md={4} xl className="px-1 mb-2">
            <SelectInput
              onChange={(e) => {
                setFilterStatus(e.target.value)
                setPage(1)
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'NORMAL', name: t('APPOINTMENT.STATUS.NORMAL') },
                { value: 'CONFIRM', name: t('APPOINTMENT.STATUS.CONFIRM') },
                { value: 'ARRIVED', name: t('APPOINTMENT.STATUS.ARRIVED') },
                { value: 'NOT_YET', name: t('APPOINTMENT.STATUS.NOT_YET') },
                { value: 'CAN_NOT_CONTACT', name: t('APPOINTMENT.STATUS.CAN_NOT_CONTACT') },
                { value: 'PUT_OFF', name: t('APPOINTMENT.STATUS.PUT_OFF') },
                { value: 'PENDING_SERVICE', name: t('APPOINTMENT.STATUS.PENDING_SERVICE') },
                { value: 'SERVICE', name: t('APPOINTMENT.STATUS.SERVICE') },
                { value: 'PENDING_PAYMENT', name: t('APPOINTMENT.STATUS.PENDING_PAYMENT') },
                { value: 'PAYMENT', name: t('APPOINTMENT.STATUS.PAYMENT') },
                { value: 'DONE', name: t('APPOINTMENT.STATUS.DONE') },
                { value: 'CANCEL', name: t('APPOINTMENT.STATUS.CANCEL') },
              ]}
              label={t('APPOINTMENT.HISTORY.APPOINTMENT_STATUS')}
              value={filterStatus}
            />
          </Col>
          <Col xs={12} sm={6} md={4} xl={3} className="px-1 mb-2">
            <InputSecondNewRangePicker
              value={rangeDate}
              inputHeight={40}
              onchange={(val: any) => {
                if (!val || !val[0] || !val[1]) {
                  setRangeDate(emptyRangePicker as [string, string])
                } else {
                  setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')] as [string, string])
                }
                setPage(1)
              }}
              allowClear
              onClear={() => {
                setRangeDate(emptyRangePicker as [string, string])
                setPage(1)
              }}
            />
          </Col>
          <Col xs={'auto'} className="px-1 mb-2">
            <MainButton
              onClick={() => searchCilck()}
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
            />
          </Col>
          {/* {showBulkPrint && (
            <Col xs={'auto'} className="px-1 mb-2">
              <MainButton
                onClick={() => props.onPrintBulk?.(Array.from(selectedIds))}
                title={t('REGISTER.BUTTON.OPD_APPOINTMENT_BULK')}
                type='primary'
                variant='outlined'
                disabled={selectedIds.size === 0}
                style={{ minWidth: '180px' }}
              />
            </Col>
          )} */}
        </Row>
        <div className="d-none d-xl-block">
          <TableConfig>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              rowCount={rowCount}
              onSort={(sortByVal: string, sortTypeVal: string) => {
                setSortType(sortTypeVal)
                setSortBy(sortByVal)
              }}
              setPageLimit={setPageLimit}
              setPage={setPage}
              headCells={headCells}
              rowsData={fetch ? [] : _.map(appointments, (val, i) => renderData(val, i))}
              textEmptyData={fetch ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(10 * 52px)' }}>
                  <CircularProgress size={30} />
                </div>
              ) as any : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(10 * 52px)' }}>
                  {t('NOT_FOUND')}
                </div>
              ) as any}
            />
          </TableConfig>
        </div>
        <div className="d-block d-xl-none">
          <THead>
            <Cell>{t('#')}</Cell>
          </THead>
          <div className={'treatment-body'} >
            <div style={{ maxHeight: 'calc(100vh - 500px)', minHeight: '400px', overflowY: 'auto' }}>
              {fetch ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
                  <CircularProgress />
                </div>
              ) : (appointments.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
                  {t('NOT_FOUND')}
                </div>
              ) : _.map(appointments, renderDataSlim))}
            </div>
            <div className={'mt-2'}>
              <TablePaginationMiniCustom
                count={rowCount || 0}
                page={page}
                rowsPerPage={pageLimit}
                rowCount={rowCount}
                pageLimit={pageLimit}
                headCells={headCells}
                onPageChange={(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)}
                setPageLimit={(event: any) => {
                  setPageLimit(parseInt(event.target.value, 10))
                  setPage(1)
                }}
                setPage={setPage}
              />
            </div>
          </div>
        </div>
      </>
    </Box>
  </PageModal >
}
